import {
  Button,
  ConfirmDialog,
  Crumbs,
  Notice,
  PageHead,
  Panel,
  PanelHead,
  Pill,
  useToast,
} from '@concord/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import type { Me } from '../../lib/session';
import { ConsoleLayout } from '../console/ConsoleLayout';
import { POOLS_KEY, PoolEditor, type PoolSummary } from './PoolEditor';
import s from './pools.module.css';

interface Member {
  id: string;
  publicId: number;
  name: string;
  enabled: boolean;
  specialty: string | null;
  city: string | null;
  addedAt: string;
}
interface Addable {
  id: string;
  publicId: number;
  name: string;
  specialty: string | null;
  city: string | null;
  treatsPoolCancers: boolean;
  hasPoolSpecialty: boolean;
}

/** One pool: what it serves, its physicians, and adding or removing them. */
export function PoolPage({ me }: { me: Me }) {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const canManage = me.permissions.includes('accounts.manage');
  const [editing, setEditing] = useState(false);
  const [retiring, setRetiring] = useState(false);
  const [removing, setRemoving] = useState<Member | null>(null);
  const [find, setFind] = useState('');

  const q = useQuery({
    queryKey: [...POOLS_KEY, id],
    queryFn: () => api<{ pool: PoolSummary; members: Member[] }>(`/staff/pools/${id}`),
  });
  const addable = useQuery({
    queryKey: [...POOLS_KEY, id, 'addable', find],
    queryFn: () =>
      api<{ items: Addable[] }>(`/staff/pools/${id}/addable?q=${encodeURIComponent(find.trim())}`),
    enabled: canManage && !!q.data?.pool.active,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: POOLS_KEY });

  const add = useMutation({
    mutationFn: (accountId: string) => api(`/staff/pools/${id}/members`, { body: { accountId } }),
    onSuccess: refresh,
    onError: (e) => toast(errorMessage(e), 'bad'),
  });
  const remove = useMutation({
    mutationFn: (accountId: string) => api(`/staff/pools/${id}/members/${accountId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await refresh();
      setRemoving(null);
    },
    onError: (e) => toast(errorMessage(e), 'bad'),
  });
  const setActive = useMutation({
    mutationFn: (active: boolean) => {
      const p = q.data!.pool;
      return api(`/staff/pools/${id}`, {
        method: 'PATCH',
        body: {
          name: p.name,
          description: p.description,
          caseTypeIds: p.caseTypes.map((t) => t.id),
          specialtyId: p.specialty?.id ?? null,
          active,
        },
      });
    },
    onSuccess: async (_r, active) => {
      await refresh();
      setRetiring(false);
      toast(active ? 'Pool restored.' : 'Pool retired.');
    },
    onError: (e) => toast(errorMessage(e), 'bad'),
  });

  const p = q.data?.pool;
  return (
    <ConsoleLayout me={me} current="pools">
      <Crumbs items={[{ label: 'Physician pools', to: '/console/pools' }, { label: p?.name ?? 'Pool' }]} />
      {q.isError && <Notice tone="bad">{errorMessage(q.error)}</Notice>}
      {p && q.data && (
        <>
          <PageHead
            title={
              <>
                {p.name} {!p.active && <Pill tone="idle">Retired</Pill>}
              </>
            }
            when={
              <>
                {p.caseTypes.length ? p.caseTypes.map((t) => t.term).join(', ') : 'Any cancer type'} ·{' '}
                {p.specialty?.term ?? 'any specialty'}
                {p.description && <div className="sub2">{p.description}</div>}
              </>
            }
            actions={
              canManage && (
                <>
                  <Button variant="sec" onClick={() => setEditing(true)}>
                    Edit
                  </Button>
                  {p.active ? (
                    <Button variant="sec" onClick={() => setRetiring(true)}>
                      Retire
                    </Button>
                  ) : (
                    <Button variant="sec" busy={setActive.isPending} onClick={() => setActive.mutate(true)}>
                      Restore
                    </Button>
                  )}
                </>
              )
            }
          />
          <div className={s.layout}>
            <Panel aria-label="Physicians in this pool">
              <PanelHead
                title="Physicians"
                sub={`${q.data.members.length} ${q.data.members.length === 1 ? 'physician' : 'physicians'}`}
              />
              {q.data.members.length ? (
                <div className="tw">
                  <table className={s.table}>
                    <thead>
                      <tr>
                        <th>Physician</th>
                        <th>Specialty</th>
                        <th>City</th>
                        {canManage && <th />}
                      </tr>
                    </thead>
                    <tbody>
                      {q.data.members.map((m) => (
                        <tr key={m.id}>
                          <td>
                            <Link to={`/console/directory/${m.id}`}>{m.name}</Link>{' '}
                            <span className="sub2">ID {m.publicId}</span>
                            {!m.enabled && (
                              <div>
                                <Pill tone="bad">Turned off</Pill>
                              </div>
                            )}
                          </td>
                          <td>{m.specialty ?? '—'}</td>
                          <td>{m.city ?? '—'}</td>
                          {canManage && (
                            <td className={s.num}>
                              <button
                                type="button"
                                className={s.link}
                                aria-label={`Remove ${m.name} from the pool`}
                                onClick={() => setRemoving(m)}
                              >
                                Remove
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className={s.empty}>No physicians in this pool yet.</p>
              )}
            </Panel>
            {canManage && p.active && (
              <Panel aria-label="Add physicians">
                <PanelHead
                  title="Add physicians"
                  sub="Those with this pool's specialty or cancer types come first"
                />
                <div className={s.add}>
                  <input
                    className="input"
                    aria-label="Find a physician by name or ID"
                    placeholder="Find by name or ID"
                    value={find}
                    onChange={(e) => setFind(e.target.value)}
                  />
                  <ul className={s.addList}>
                    {addable.data?.items.map((a) => (
                      <li key={a.id}>
                        <div>
                          <strong>{a.name}</strong> <span className="sub2">ID {a.publicId}</span>
                          <div className="sub2">
                            {a.specialty ?? 'No specialty'}
                            {a.city ? ` · ${a.city}` : ''}
                            {a.hasPoolSpecialty && ' · this specialty'}
                            {a.treatsPoolCancers && ' · treats these cancers'}
                          </div>
                        </div>
                        <button
                          type="button"
                          className={s.addBtn}
                          aria-label={`Add ${a.name}`}
                          disabled={add.isPending}
                          onClick={() => add.mutate(a.id)}
                        >
                          + Add
                        </button>
                      </li>
                    ))}
                    {addable.data && addable.data.items.length === 0 && (
                      <li className="sub2">No other physician matches.</li>
                    )}
                  </ul>
                </div>
              </Panel>
            )}
          </div>
          <PoolEditor open={editing} onOpenChange={setEditing} pool={p} />
          <ConfirmDialog
            open={retiring}
            onOpenChange={setRetiring}
            title={`Retire ${p.name}?`}
            consequence="It is no longer offered on Build the panel. Its physicians and the panels already built stay as they are, and you can restore it later."
            confirmLabel="Retire pool"
            busy={setActive.isPending}
            onConfirm={() => setActive.mutate(false)}
          />
          <ConfirmDialog
            open={!!removing}
            onOpenChange={(v) => !v && setRemoving(null)}
            title={`Remove ${removing?.name ?? ''} from ${p.name}?`}
            consequence="They stay in the directory and on any panel they are already on. You can add them back at any time."
            confirmLabel="Remove"
            busy={remove.isPending}
            onConfirm={() => removing && remove.mutate(removing.id)}
          />
        </>
      )}
    </ConsoleLayout>
  );
}
